import {
  Archive,
  ArchiveRestore,
  ArrowRight,
  FolderTree,
  MoreVertical,
  Move,
  Pencil,
  Plus,
  Sprout,
  Warehouse,
} from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  CAPABILITY_SOON_HINT,
  canArchiveFarm,
  formatHa,
  isCapable,
  pluralFarms,
  pluralFields,
} from '../structureLogic';
import type { ClusterNode, FarmNode, StructureTree as Tree } from '../types';

interface FarmActions {
  onRename: (farmId: string) => void;
  onMove: (farmId: string) => void;
  onOpen: (farmId: string) => void;
  onArchive: (farmId: string) => void;
  onRestore: (farmId: string) => void;
  /** AK-584 — «＋ Добавить поле»: карта с предвыбранным хозяйством. */
  onAddField: (farmId: string) => void;
}

interface StructureTreeProps extends FarmActions {
  tree: Tree;
  /** Всего активных хозяйств компании — для правила «последнее хозяйство». */
  activeFarmCount: number;
  /** Показывать ли kebab-меню действий (админ). */
  canManage: boolean;
}

/**
 * Строка со статистикой: «N полей · X га».
 *
 * У пустого узла — прочерк вместо «0 полей · 0 га»: на свежем тенанте эта
 * фраза повторялась 11 строк подряд и читалась как колонка нулей, в которой
 * не за что зацепиться взглядом (design review 2026-08-18, зона 2 · А9).
 */
function Stat({ fields, ha }: { fields: number; ha: number }) {
  if (fields === 0) {
    return (
      <span
        className="ml-auto shrink-0 text-xs text-muted-foreground"
        title="Полей пока нет"
      >
        —
      </span>
    );
  }
  return (
    <span className="ml-auto flex shrink-0 items-center gap-2 text-xs tabular-nums text-muted-foreground">
      <span>
        <span className="font-medium text-foreground/80">{fields}</span>{' '}
        {pluralFields(fields)}
      </span>
      <span aria-hidden>·</span>
      <span>
        <span className="font-medium text-foreground/80">{formatHa(ha)}</span> га
      </span>
    </span>
  );
}

function FarmRow({
  farm,
  nested,
  activeFarmCount,
  canManage,
  onRename,
  onMove,
  onOpen,
  onArchive,
  onRestore,
  onAddField,
}: {
  farm: FarmNode;
  nested: boolean;
  activeFarmCount: number;
  canManage: boolean;
} & FarmActions) {
  const archiveRule = canArchiveFarm(activeFarmCount);
  // AK-584: пустой узел сам зовёт себя заполнить. У хозяйства с полями кнопки
  // нет — только меню «⋯», иначе строка превращается в панель кнопок.
  const showAddFieldButton =
    canManage && !farm.archived && farm.field_count === 0;
  return (
    <div
      className={cn(
        'flex items-center gap-2.5 border-b border-border/60 px-3.5 py-2.5 text-sm last:border-b-0',
        nested ? 'pl-12' : 'pl-8',
        farm.archived && 'opacity-60',
      )}
    >
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-primary/10 text-primary">
        <Warehouse className="h-3 w-3" />
      </span>
      <Link
        to="/farm/$farmId"
        params={{ farmId: farm.id }}
        className="truncate text-left font-medium hover:underline"
      >
        {farm.name}
      </Link>
      {farm.archived && (
        <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
          В архиве
        </Badge>
      )}
      <Stat fields={farm.field_count} ha={farm.area_ha} />
      {/* AK-902: поля, техника и отделения хозяйства смотрят в «Данных
          хозяйства» (`/farm/<id>`) — здесь вторая карточка не живёт, только
          ссылка. Правка реквизитов остаётся в drawer (меню «Переименовать»). */}
      <Button asChild variant="link" size="sm" className="h-7 shrink-0 px-1">
        <Link
          to="/farm/$farmId"
          params={{ farmId: farm.id }}
          data-testid={`structure-farm-data-${farm.id}`}
          aria-label={`Данные хозяйства · ${farm.name}`}
        >
          <span className="hidden sm:inline">Данные хозяйства</span>
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </Button>
      {showAddFieldButton && (
        <Button
          size="sm"
          className="h-7 shrink-0"
          data-testid={`structure-add-field-${farm.id}`}
          onClick={() => onAddField(farm.id)}
          aria-label={`Добавить поле · ${farm.name}`}
        >
          <Plus className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Поле</span>
        </Button>
      )}
      {canManage && farm.archived && (
        <Button
          variant="outline"
          size="sm"
          className="h-7 shrink-0"
          onClick={() => onRestore(farm.id)}
          aria-label={`Восстановить · ${farm.name}`}
        >
          <ArchiveRestore className="h-3.5 w-3.5 text-accent-brand" />
          <span className="hidden sm:inline">Восстановить</span>
        </Button>
      )}
      {canManage && !farm.archived && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7 shrink-0"
              aria-label={`Действия · ${farm.name}`}
            >
              <MoreVertical className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {/* AK-584 — первым пунктом, отдельно от служебных действий: это
                то, ради чего хозяйство и заводили. */}
            <DropdownMenuItem onSelect={() => onAddField(farm.id)}>
              <Plus className="h-4 w-4 text-accent-brand" />
              Добавить поле
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => onRename(farm.id)}>
              <Pencil className="h-4 w-4" />
              Переименовать
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onMove(farm.id)}>
              <Move className="h-4 w-4 text-accent-brand" />
              Переместить в кластер
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onOpen(farm.id)}>
              <ArrowRight className="h-4 w-4" />
              Данные хозяйства
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {archiveRule.allowed ? (
              <DropdownMenuItem
                onSelect={() => onArchive(farm.id)}
                className="text-destructive focus:text-destructive"
              >
                <Archive className="h-4 w-4" />
                Архивировать
              </DropdownMenuItem>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <DropdownMenuItem
                      disabled
                      className="text-muted-foreground"
                      onSelect={(e) => e.preventDefault()}
                    >
                      <Archive className="h-4 w-4" />
                      Архивировать
                    </DropdownMenuItem>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="left">{archiveRule.reason}</TooltipContent>
              </Tooltip>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

function ClusterGroup({
  cluster,
  activeFarmCount,
  canManage,
  actions,
}: {
  cluster: ClusterNode;
  activeFarmCount: number;
  canManage: boolean;
  actions: FarmActions;
}) {
  return (
    <>
      <div className="flex items-center gap-2.5 border-b border-border/60 bg-secondary/30 px-3.5 py-2.5 pl-6 text-sm">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-accent-brand/15 text-accent-brand">
          <FolderTree className="h-3 w-3" />
        </span>
        <span className="font-semibold">{cluster.name}</span>
        <span className="text-xs text-muted-foreground">
          {cluster.farm_count} {pluralFarms(cluster.farm_count)}
        </span>
        <Stat fields={cluster.field_count} ha={cluster.area_ha} />
      </div>
      {cluster.farms.map((f) => (
        <FarmRow
          key={f.id}
          farm={f}
          nested
          activeFarmCount={activeFarmCount}
          canManage={canManage}
          {...actions}
        />
      ))}
    </>
  );
}

/**
 * Дерево структуры: компания → кластеры → хозяйства. Хозяйства «вне кластера»
 * — отдельной группой. На каждом уровне число полей и га. Kebab-меню действий
 * (переименовать / переместить / открыть / архивировать) — только для админа
 * (canManage); архив последнего хозяйства заблокирован с тултипом. Архивные
 * строки (при тумблере «Показать архивные») — приглушены, с бейджем «В архиве»
 * и кнопкой «Восстановить» (AK-340).
 */
export function StructureTree({
  tree,
  activeFarmCount,
  canManage,
  onRename,
  onMove,
  onOpen,
  onArchive,
  onRestore,
  onAddField,
}: StructureTreeProps) {
  const actions: FarmActions = {
    onRename,
    onMove,
    onOpen,
    onArchive,
    onRestore,
    onAddField,
  };

  return (
    <div className="overflow-hidden rounded-lg border border-border">
      {/* Компания */}
      {tree.company && (
        <div className="flex items-center gap-2.5 border-b border-border/60 px-3.5 py-3 text-sm">
          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-foreground text-background">
            <Sprout className="h-3 w-3" />
          </span>
          <span className="font-semibold">{tree.company.name}</span>
          <span className="ml-auto flex flex-wrap items-center justify-end gap-2 text-xs tabular-nums text-muted-foreground">
            <span>
              <span className="font-medium text-foreground/80">
                {tree.company.cluster_count}
              </span>{' '}
              кластера
            </span>
            <span aria-hidden>·</span>
            <span>
              <span className="font-medium text-foreground/80">
                {tree.company.farm_count}
              </span>{' '}
              {pluralFarms(tree.company.farm_count)}
            </span>
            <span aria-hidden>·</span>
            <span>
              <span className="font-medium text-foreground/80">
                {tree.company.field_count}
              </span>{' '}
              {pluralFields(tree.company.field_count)} ·{' '}
              <span className="font-medium text-foreground/80">
                {formatHa(tree.company.area_ha)}
              </span>{' '}
              га
            </span>
          </span>
        </div>
      )}

      {tree.clusters.map((c) => (
        <ClusterGroup
          key={c.id}
          cluster={c}
          activeFarmCount={activeFarmCount}
          canManage={canManage}
          actions={actions}
        />
      ))}

      {/* Вне кластера */}
      {tree.noCluster.length > 0 && (
        <>
          <div className="flex items-center gap-2.5 border-b border-border/60 bg-secondary/30 px-3.5 py-2.5 pl-6 text-sm">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-secondary text-muted-foreground">
              —
            </span>
            <span className="font-medium text-muted-foreground">Вне кластера</span>
            <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
              без кластера
            </Badge>
            <span className="ml-auto text-xs text-muted-foreground">
              {tree.noCluster.length} {pluralFarms(tree.noCluster.length)}
            </span>
          </div>
          {tree.noCluster.map((f) => (
            <FarmRow
              key={f.id}
              farm={f}
              nested={false}
              activeFarmCount={activeFarmCount}
              canManage={canManage}
              {...actions}
            />
          ))}
        </>
      )}
    </div>
  );
}

export { isCapable, CAPABILITY_SOON_HINT };
